import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, HelpCircle } from "lucide-react";

export default function SaasFAQ() {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      q: "Can I use my own domain?",
      a: "Yes. Enterprise plans support complete domain white-labeling. You can map custom subdomains (e.g. lms.youracademy.com) or primary domains, and Skillzy automatically provisions and renews SSL certificates for you."
    },
    {
      q: "Do you support online exams?",
      a: "Absolutely. Skillzy supports secure online MCQ, speed quizzes, and multiple-choice mock series with integrated browser lockdowns, automated schedules, and strict timing control."
    },
    {
      q: "Do you support subjective exams?",
      a: "Yes, this is one of our key strengths. Students can draw on a digital canvas using a stylus, solve math equations, or scan and upload handwritten answer papers. Teachers evaluate using our custom side-by-side drawing and annotation tools."
    },
    {
      q: "Is white-label available?",
      a: "Yes. Our white-labeling options let you remove all Skillzy branding, apply your custom color scheme, system-wide logo, custom email footers, and publish student portals under your private company name."
    },
    {
      q: "Can multiple institutes use the platform?",
      a: "Yes. Skillzy is built from the ground up as a multi-tenant platform. A school group or franchisor can run dozens of independent schools or coaching branches (tenants) under a single consolidated administrative panel."
    }
  ];

  return (
    <section id="faq" className="py-24 bg-white dark:bg-slate-950 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-50 dark:bg-violet-950/40 border border-violet-100 dark:border-violet-900/50 rounded-full text-violet-600 dark:text-violet-400 text-sm font-bold mb-4">
            <HelpCircle size={16} />
            <span>Support & Documentation</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 dark:text-white tracking-tight">
            Frequently Asked Questions
          </h2>
          <p className="mt-4 text-lg text-slate-600 dark:text-slate-300 font-medium">
            Got questions about integrations or domain mapping? Here are quick answers to the most common queries.
          </p>
        </div>

        {/* Accordion List */}
        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq, idx) => {
            const isOpen = openIndex === idx;
            return (
              <div
                key={faq.q}
                className="border border-slate-100 dark:border-slate-800 rounded-2xl bg-slate-50 dark:bg-slate-900/40 overflow-hidden transition-all duration-300"
              >
                {/* Header toggle */}
                <button
                  onClick={() => setOpenIndex(isOpen ? null : idx)}
                  className="w-full flex items-center justify-between p-6 text-left"
                >
                  <span className="text-sm sm:text-base font-black text-slate-900 dark:text-white">
                    {faq.q}
                  </span>
                  <div className={`p-1.5 rounded-lg ${isOpen ? "bg-violet-600 text-white" : "bg-white dark:bg-slate-950 text-slate-400"} shadow-sm shrink-0`}>
                    {isOpen ? <Minus size={16} /> : <Plus size={16} />}
                  </div>
                </button>

                {/* Animated content expansion */}
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                      <div className="px-6 pb-6 text-xs sm:text-sm text-slate-550 dark:text-slate-400 leading-relaxed font-semibold border-t border-slate-100 dark:border-slate-850 pt-4">
                        {faq.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
}
